import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ─── Types ───────────────────────────────────────────────────────────

export interface Ytd {
  budget: number;
  actual: number;
  variance: number;
  pct: number | null;
}

export interface ClassBlock {
  class_name: string;
  ytd: Ytd;
  fy_budget: number;
  monthly: { budget: number[]; actual: number[] };
}

export interface BudgetRef {
  id: string;
  name: string;
  source: "auto" | "override";
}

export interface SummaryResponse {
  year: number;
  budget: BudgetRef;
  refreshed_at: string;
  months: string[];
  complete_months: number;
  include_current: boolean;
  scope: { all_classes: boolean; classes: string[]; include_revenue: boolean };
  expense: { classes: ClassBlock[]; total: ClassBlock; total_is_company: boolean };
  revenue: { classes: ClassBlock[]; total: ClassBlock } | null;
}

export interface AccountBlock extends ClassBlock {
  account_number: string | null;
  account_name: string | null;
}

export interface CategoryBlock {
  kind: "Expense" | "Revenue";
  category: string;
  ytd: Ytd;
  fy_budget: number;
  monthly: { budget: number[]; actual: number[] };
  accounts: AccountBlock[];
}

export interface DepartmentResponse {
  year: number;
  class_name: string;
  budget: BudgetRef;
  refreshed_at: string;
  months: string[];
  complete_months: number;
  categories: CategoryBlock[];
}

export interface GlTransaction {
  transaction_date: string;
  transaction_type: string | null;
  document_id: string | null;
  counterparty_name: string | null;
  memo: string | null;
  amount: number;
}

export interface UserGrants {
  user_id: string;
  email: string;
  name: string;
  classes: string[];
}

export interface GrantableUser {
  user_id: string;
  email: string;
  name: string;
}

export interface QbBudget {
  budget_id: string;
  budget_name: string;
  budget_type: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

// ─── Hooks ───────────────────────────────────────────────────────────

const KEY = ["budget-vs-actual"] as const;

export function useBvaSummary(year: number, includeCurrent: boolean) {
  return useQuery({
    queryKey: [...KEY, "summary", year, includeCurrent],
    queryFn: () =>
      apiFetch<SummaryResponse>(
        `/budget-vs-actual/summary?year=${year}&include_current=${includeCurrent}`,
      ),
  });
}

export function useBvaDepartment(year: number, className: string | null, includeCurrent: boolean) {
  return useQuery({
    queryKey: [...KEY, "department", year, className, includeCurrent],
    queryFn: () =>
      apiFetch<DepartmentResponse>(
        `/budget-vs-actual/department/${encodeURIComponent(className ?? "")}?year=${year}&include_current=${includeCurrent}`,
      ),
    enabled: className != null,
  });
}

export function useBvaTransactions(
  className: string | null,
  account: string | null,
  period: string | null,
) {
  return useQuery({
    queryKey: [...KEY, "transactions", className, account, period],
    queryFn: () =>
      apiFetch<{ transactions: GlTransaction[] }>(
        `/budget-vs-actual/transactions?class=${encodeURIComponent(className ?? "")}&account=${encodeURIComponent(account ?? "")}&period=${period}`,
      ),
    enabled: className != null && account != null && period != null,
    staleTime: 10 * 60 * 1000,
  });
}

export interface GlossaryDef {
  slug: string;
  term: string;
  definition: string;
}

/** Glossary term map (lowercased name/alias → definition) for line-item
 *  tooltips. Curated in the Glossary feature. */
export function useBvaDefinitions(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "definitions"],
    queryFn: () =>
      apiFetch<{ definitions: Record<string, GlossaryDef> }>("/budget-vs-actual/definitions"),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function useBvaRefresh(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ refreshed_at: string }>(`/budget-vs-actual/refresh?year=${year}`, {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// ─── Admin hooks ─────────────────────────────────────────────────────

export function useBvaGrants(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "grants"],
    queryFn: () => apiFetch<{ grants: UserGrants[] }>("/budget-vs-actual/admin/grants"),
    enabled,
  });
}

export function useBvaUsers(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "users"],
    queryFn: () => apiFetch<{ users: GrantableUser[] }>("/budget-vs-actual/admin/users"),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBvaClasses(enabled: boolean, year: number) {
  return useQuery({
    queryKey: [...KEY, "classes", year],
    queryFn: () =>
      apiFetch<{ classes: string[] }>(`/budget-vs-actual/admin/classes?year=${year}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSetGrants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { user_id: string; classes: string[] }) =>
      apiFetch<UserGrants>("/budget-vs-actual/admin/grants", {
        method: "PUT",
        body: payload,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useBvaBudgets(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "budgets"],
    queryFn: () => apiFetch<{ budgets: QbBudget[] }>("/budget-vs-actual/admin/budgets"),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSetBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { year: number; budget_id: string | null }) =>
      apiFetch<BudgetRef>("/budget-vs-actual/admin/budget", { method: "PUT", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
